import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CreateTrialState } from 'types/createTrial.type';

import { Step } from '../../types/stepFlow.type';

// Helper function to filter out keys ending in _files so we don't store binaries in state
// This happens when an upload field is wrapped in Formik, i.e. abline_files
const filterOutFileKeys = (obj: Record<string, any>): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !key.endsWith('_files')),
  );
};

const initialState: CreateTrialState = {
  trial: {},
  steps: {},
  completedSteps: {},
};

export const createTrialSlice = createSlice({
  name: 'createTrial',
  initialState,
  reducers: {
    updateStep: (state, action: PayloadAction<Step>) => {
      const { index, data } = action.payload;
      state.steps[index] = data;
      state.completedSteps[index] = true;
      state.trial = { ...state.trial, ...data };
    },
    clearStep: (state, action: PayloadAction<Step>) => {
      const { index, data } = action.payload;

      delete state.steps[index];
      state.completedSteps[index] = false;
      state.trial = data;
    },
    updateTrialData: (state, action: PayloadAction<Step['data']>) => {
      state.trial = { ...state.trial, ...action.payload };
    },
    clearTrialData: (state) => {
      state.steps = {};
      state.completedSteps = {};
      state.trial = {};
    },
  },
});

// Export the raw actions for type information
const {
  updateStep: rawUpdateStep,
  clearStep,
  updateTrialData: rawUpdateTrialData,
  clearTrialData,
} = createTrialSlice.actions;

// Create wrapped action creators that filter before dispatch
export const updateStep = (step: Step) => {
  const { index, data } = step;
  return rawUpdateStep({
    index,
    data: filterOutFileKeys(data),
  });
};

export const updateTrialData = (data: Step['data']) => {
  return rawUpdateTrialData(filterOutFileKeys(data));
};

// Re-export the unmodified actions
export { clearStep, clearTrialData };

export default createTrialSlice.reducer;
